import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { cambiarSesion, type AccesoContribuyente, type DespachoDisponible, type Sesion } from '@/dominio/sesion/sesion'
import type { TenantResumen } from './BarraSuperior'

/**
 * El selector de la barra superior con los despachos dentro (`docs/adr/0021`).
 *
 * Un despacho va en la misma lista que los contribuyentes, arriba, con el
 * identificador marcado (`despacho:<id>`): es otra sesión a la que se pasa con
 * el mismo `Ctrl+K`. Los clientes a los que se llega por un despacho dicen por
 * cuál.
 */

const PREFIJO_DESPACHO = 'despacho:'

export function filaDeDespacho(d: DespachoDisponible): TenantResumen {
  return {
    id: `${PREFIJO_DESPACHO}${d.despacho}`,
    nombre: d.nombre,
    rnc: d.identificacion,
    semaforo: 'marca',
    motivo: d.estado === 'suspendido' ? 'Despacho suspendido en la plataforma' : 'Despacho · cartera de clientes',
  }
}

export function filaDeContribuyente(a: AccesoContribuyente): TenantResumen {
  return {
    id: a.tenant,
    nombre: a.razonSocial,
    rnc: a.rnc,
    // `neutral`, no `verde`: el semáforo de cada cliente está en la cartera, y
    // pintar verde aquí sería afirmar «todo en orden» sin haberlo mirado.
    semaforo: 'neutral',
    motivo: a.despachoNombre ? `Vía ${a.despachoNombre}` : undefined,
  }
}

export function filasDelSelector(sesion: Sesion): TenantResumen[] {
  return [...sesion.despachos.map(filaDeDespacho), ...sesion.disponibles.map(filaDeContribuyente)]
}

/**
 * Pasa a otro contribuyente o a un despacho **sin volver a pedir la
 * credencial**: el servidor emite otro testigo con el vigente.
 *
 * Lo cacheado era del contribuyente (o del despacho) anterior y no puede
 * pintarse un instante bajo el nuevo: se vacía la caché antes de navegar.
 */
export function useCambiarDeSesion() {
  const navegar = useNavigate()
  const cliente = useQueryClient()
  const [cambiando, setCambiando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cambiar = async (id: string): Promise<void> => {
    setError(null)
    setCambiando(true)

    try {
      const esDespacho = id.startsWith(PREFIJO_DESPACHO)
      await cambiarSesion(esDespacho ? { despacho: id.slice(PREFIJO_DESPACHO.length) } : { tenant: id })
      cliente.clear()
      await navegar({ to: esDespacho ? '/despacho' : '/inicio' })
    } catch {
      setError('No se pudo cambiar: esta cuenta ya no tiene acceso ahí, o se perdió la conexión. La sesión sigue donde estaba.')
    } finally {
      setCambiando(false)
    }
  }

  return { cambiar, cambiando, error, limpiarError: () => setError(null) }
}
